import { MOCK, MOCK_ANSWERS, MOCK_ME, MOCK_SIGNIN_MESSAGE, MOCK_TG_CODE } from "./mock";
import type { Facts, Me, TgCode, TgStatus } from "./types";

export const API = "/api/hrai";

/** Thrown when the worker reports the session is gone — the app signs out. */
export class SignedOutError extends Error {
  constructor() {
    super("signed out");
    this.name = "SignedOutError";
  }
}

// The embed token and chat session are minted once per page load and reused
// for every question. Both are session-scoped: /token reads the HttpOnly
// cookie and mints an identity-bound claim, never exposing the bearer.
let token: string | null = null;
let sid: string | null = null;
let me: Me | null = null;

export function setMe(next: Me | null) {
  me = next;
}

export function resetSession() {
  token = null;
  sid = null;
}

export async function ensureSession(): Promise<void> {
  if (!token) {
    const r = await fetch("/token");
    const t = await r.json();
    if (r.status === 401) throw new SignedOutError();
    if (!t.token) throw new Error("token mint failed: " + JSON.stringify(t));
    token = t.token;
  }
  if (!sid) {
    const r = await fetch(`${API}/sessions`, {
      method: "POST",
      headers: { Authorization: "Bearer " + token, "Content-Type": "application/json" },
      body: JSON.stringify({
        title: "employee portal (" + (me ? me.employee_slug : "unknown") + ")",
      }),
    });
    const d = await r.json();
    if (!d.session) throw new Error("session create failed: " + JSON.stringify(d));
    sid = d.session.id;
  }
}

export function askUrl(): string {
  return `${API}/sessions/${sid}/ask`;
}

export function authHeaders(): Record<string, string> {
  return { Authorization: "Bearer " + token, "Content-Type": "application/json" };
}

/* ============ identity ============ */

export async function getMe(): Promise<Me | null> {
  if (MOCK) return MOCK_ME;
  const r = await fetch("/api/portal/me");
  if (!r.ok) return null;
  return r.json();
}

export interface SigninResult {
  message: string;
  dev?: boolean;
  link?: string;
}

export async function requestSignin(email: string): Promise<SigninResult> {
  if (MOCK) {
    return { message: MOCK_SIGNIN_MESSAGE, dev: true, link: "#mock-verify" };
  }
  const r = await fetch("/auth/request", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ email }),
  });
  const d = await r.json();
  if (!r.ok) throw new Error(d.error || "error " + r.status);
  return { message: d.message || "Check your inbox for your sign-in link.", dev: d.dev, link: d.link };
}

export async function signOut(): Promise<void> {
  if (!MOCK) {
    try {
      await fetch("/auth/logout", { method: "POST" });
    } catch {}
  }
  resetSession();
  location.href = location.pathname;
}

/* ============ records ============
 * The record tabs read the employee's own synced HR record through the
 * portal's session-scoped facts endpoint: identity from the HttpOnly cookie,
 * figures from the last HR-system sync, sentence composed server-side. No
 * model sits in this path, so a tab answers immediately.
 */
let factsCache: Facts | null = null;

export async function loadFacts(force?: boolean): Promise<Facts> {
  if (MOCK) return MOCK_ANSWERS;
  if (factsCache && !force) return factsCache;
  const r = await fetch("/api/portal/facts", { headers: { Accept: "application/json" } });
  if (!r.ok) throw new Error("could not read your records (" + r.status + ")");
  const d = await r.json();
  if (d.unknown) throw new Error(d.message);
  factsCache = d;
  return d;
}

/* ============ Telegram ============ */

export async function tgStatus(mockLinked: boolean): Promise<TgStatus> {
  if (MOCK) {
    return { linked: mockLinked, linked_at: mockLinked ? MOCK_ME.synced_at : null };
  }
  try {
    const r = await fetch("/api/portal/tg-status");
    return r.ok ? await r.json() : { linked: false };
  } catch {
    return { linked: false };
  }
}

export async function tgCode(): Promise<TgCode> {
  if (MOCK) return MOCK_TG_CODE;
  const r = await fetch("/api/portal/tg-code", { method: "POST" });
  const d = await r.json();
  if (!r.ok) throw new Error(d.error || "error " + r.status);
  return d;
}

export async function tgRevoke(): Promise<void> {
  if (MOCK) return;
  try {
    await fetch("/api/portal/tg-revoke", { method: "POST" });
  } catch {}
}
